import type { CSSProperties } from 'react';

import classNames from 'classnames';

import type { PostTheme } from '@/index';
import { usePostContentContext } from '@/postContentElement/context';
import { evaluateAndAppendUTMParams } from '@/utils/evaluateAndAppendUTMParams';

import { useAnchorable } from '../../hooks/useAnchorable';

import { appendProtocol, isAnchorLink, isMailToLink, isTelLink } from '../../utils/link';

import { button, buttonContainer } from './Button.css';
import type { TPostButtonProps } from './Button.types';

export {
  button as postButtonClass,
  buttonContainer as postButtonContainerClass,
} from './Button.css';

export interface PostButtonEditorRenderProps {
  className: string;
  style: React.CSSProperties;
}

type PostButtonProps = Omit<TPostButtonProps, 'children'> & {
  editorMode?: boolean;
  children: React.ReactNode | ((renderProps: PostButtonEditorRenderProps) => React.ReactNode);
};

const getLineHeight = (size?: 'normal' | 'large' | 'small', postTheme?: PostTheme): string => {
  if (!postTheme || postTheme.theme_version !== '2') {
    if (size === 'small') return '32px';
    if (size === 'normal') return '42px';
    if (size === 'large') return '52px';
    return '32px';
  }

  const baseFontSize = Math.abs(Number(postTheme.button_font_size));

  let fontSizeAdjustment = 0;
  if (size === 'small') {
    fontSizeAdjustment = -2;
  }

  if (size === 'large') {
    fontSizeAdjustment = 2;
  }

  return `${Math.max(baseFontSize + fontSizeAdjustment, 1)}px`;
};

const getPadding = (size?: 'normal' | 'large' | 'small', postTheme?: PostTheme): string => {
  if (!postTheme || postTheme.theme_version !== '2') {
    if (size === 'small') return '0px 12px';
    if (size === 'normal') return '0px 14px';
    if (size === 'large') return '0px 22px';
    return '0px 14px';
  }

  let paddingTopBottomAdjustment = '0px';
  let paddingLeftRightAdjustment = '0px';

  if (size === 'small') {
    paddingTopBottomAdjustment = '-2px';
    paddingLeftRightAdjustment = '-3px';
  }
  if (size === 'large') {
    paddingTopBottomAdjustment = '4px';
    paddingLeftRightAdjustment = '8px';
  }

  const top = `calc(var(--wt-button-padding-top) + ${paddingTopBottomAdjustment})`;
  const right = `calc(var(--wt-button-padding-right) + ${paddingLeftRightAdjustment})`;
  const bottom = `calc(var(--wt-button-padding-bottom) + ${paddingTopBottomAdjustment})`;
  const left = `calc(var(--wt-button-padding-left) + ${paddingLeftRightAdjustment})`;

  return `${top} ${right} ${bottom} ${left}`;
};

const getFontSize = (size?: 'normal' | 'large' | 'small', postTheme?: PostTheme): string => {
  if (!postTheme || postTheme.theme_version !== '2') {
    if (size === 'small') return 'calc(var(--wt-paragraph-font-size) - 2px)';
    if (size === 'normal') return 'var(--wt-paragraph-font-size)';
    if (size === 'large') return 'calc(var(--wt-paragraph-font-size) + 2px)';
    return 'var(--wt-paragraph-font-size)';
  }

  const baseFontSize = Math.abs(Number(postTheme?.button_font_size));

  let fontSizeAdjustment = 0;
  if (size === 'small') {
    fontSizeAdjustment = -2;
  }
  if (size === 'large') {
    fontSizeAdjustment = 2;
  }

  return `${Math.max(baseFontSize + fontSizeAdjustment, 1)}px`;
};

const getRadius = (postTheme?: PostTheme): string => {
  if (!postTheme || postTheme.theme_version !== '2') {
    return 'var(--wt-button-border-radius, 10px)';
  }

  const topLeft = Math.abs(Number(postTheme.button_border_radius_top_left));
  const topRight = Math.abs(Number(postTheme.button_border_radius_top_right));
  const bottomRight = Math.abs(Number(postTheme.button_border_radius_bottom_right));
  const bottomLeft = Math.abs(Number(postTheme.button_border_radius_bottom_left));

  if ([topLeft, topRight, bottomRight, bottomLeft].every((value) => value === 0)) {
    return '0px';
  }

  if (topLeft === topRight && topRight === bottomRight && bottomRight === bottomLeft) {
    return `${topLeft}px`;
  }

  if (topLeft === bottomRight && topRight === bottomLeft) {
    return `${topLeft}px ${topRight}px`;
  }

  return `${topLeft}px ${topRight}px ${bottomRight}px ${bottomLeft}px`;
};

const getBorderStyles = (postTheme?: PostTheme): CSSProperties => {
  if (!postTheme || postTheme.theme_version !== '2') {
    return {
      border: 'solid var(--wt-button-border-width) var(--wt-button-border-color)',
    };
  }

  const topWidth = Math.abs(Number(postTheme.button_border_top_width));
  const rightWidth = Math.abs(Number(postTheme.button_border_right_width));
  const bottomWidth = Math.abs(Number(postTheme.button_border_bottom_width));
  const leftWidth = Math.abs(Number(postTheme.button_border_left_width));

  if ([topWidth, rightWidth, bottomWidth, leftWidth].every((value) => value === 0)) return {};

  return {
    borderWidth: `${topWidth}px ${rightWidth}px ${bottomWidth}px ${leftWidth}px`,
    borderStyle: 'solid',
    borderColor: postTheme.button_border_color,
  };
};

export const PostButton = (props: PostButtonProps) => {
  const { postTheme } = usePostContentContext();

  const { element, children, editorMode } = props;
  const { attrs } = element;
  const { href, size, alignment, fullWidth, customTextColor, customBackgroundColor } = attrs;
  const { anchorAttributes } = useAnchorable(attrs);
  const { utmParams } = usePostContentContext();

  const buttonClassNames = classNames(button, fullWidth && 'w-full');

  const containerCssVars = {
    '--button-line-height': getLineHeight(size, postTheme),
    '--button-padding': getPadding(size, postTheme),
    '--button-font-size': getFontSize(size, postTheme),
    '--post-button-radius': getRadius(postTheme),
    '--button-alignment': alignment,
    '--custom-background-color': customBackgroundColor,
    '--custom-text-color': customTextColor,
  } as CSSProperties;

  const renderProps: PostButtonEditorRenderProps = {
    className: buttonClassNames,
    style: getBorderStyles(postTheme),
  };

  if (editorMode) {
    return (
      <div {...anchorAttributes} className={buttonContainer} style={containerCssVars}>
        {typeof children === 'function' ? children(renderProps) : children}
      </div>
    );
  }

  // Anchor related
  const hrefWithUtmParams = evaluateAndAppendUTMParams(appendProtocol(href, 'http://'), utmParams);
  const target = attrs.target !== '_self' ? '_blank' : undefined;
  const isClickTrackingDisabled =
    attrs?.clicktracking === 'off' ||
    isAnchorLink(attrs?.href || '') ||
    isMailToLink(attrs?.href || '') ||
    isTelLink(attrs?.href || '');
  const anchorAdditionalAttributes = {};
  if (isClickTrackingDisabled) {
    anchorAdditionalAttributes['data-skip-tracking'] = 'true';
  }

  return (
    <div {...anchorAttributes} className={buttonContainer} style={containerCssVars}>
      {hrefWithUtmParams ? (
        <a
          href={hrefWithUtmParams}
          className={buttonClassNames}
          style={{ ...getBorderStyles(postTheme) }}
          type="button"
          target={target}
          {...anchorAdditionalAttributes}
        >
          {typeof children === 'function' ? children(renderProps) : children}
        </a>
      ) : (
        <button type="button" className={buttonClassNames}>
          {typeof children === 'function' ? children(renderProps) : children}
        </button>
      )}
    </div>
  );
};
